import { useCallback, useState } from 'react'
import useSWR, { mutate } from 'swr'
import type {
  UserReward,
  UserRewardItem,
  UserRewardsResponse,
  CreateUserRewardDTO,
  UpdateUserRewardDTO,
  UserRewardReport,
  UserRewardReportExpanded,
  EmployeeRewardsSummary,
  EmployeeRewardsSummariesResponse,
  OrganizationCurrencySettings,
  AllUserRewardReportsResponse,
} from 'types/rewards'
import { deleteWithToken, fetchWithToken, postWithToken, putWithToken } from './http'

// ============================================================================
// User Rewards
// ============================================================================

interface UseFetchUserRewardsReturn {
  userRewards: UserRewardItem[] | null
  packageInfo: UserRewardsResponse['package'] | null
  currencySettings: OrganizationCurrencySettings | null
  revision: number
  isHidden: boolean
  isLoading: boolean
  isError: any
  refetch: () => Promise<UserRewardsResponse | null>
}

export function useFetchUserRewards(
  userId: string,
  periodId: string,
  token: string,
  shouldFetch: boolean = true
): UseFetchUserRewardsReturn {
  const reallyShouldFetch = userId?.trim() !== '' && periodId?.trim() !== '' && token?.trim() !== '' && shouldFetch

  const { data, error, isValidating, mutate } = useSWR<UserRewardsResponse>(
    reallyShouldFetch ? [`rewards/users/${userId}/periods/${periodId}`, token] : null,
    ([url, tkn]: [string, string]) => fetchWithToken(url, tkn, null),
    {
      revalidateIfStale: true,
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      dedupingInterval: 0,
    }
  )

  const refetch = async (): Promise<UserRewardsResponse | null> => {
    const refetchedData = await mutate()
    return refetchedData || null
  }

  return {
    userRewards: data?.userReward?.rewards || null,
    packageInfo: data?.package || null,
    currencySettings: data?.currencySettings || null,
    revision: data?.userReward?.revision || 0,
    isHidden: data?.userReward?.isHidden || false,
    isLoading: !data && isValidating,
    isError: error,
    refetch,
  }
}

interface UseFetchUserRewardReportReturn {
  report: UserRewardReport | null
  isLoading: boolean
  isError: any
  refetch: () => Promise<UserRewardReport | null>
}

export function useFetchUserRewardReport(
  userId: string,
  periodId: string,
  organizationId: string,
  token: string,
  shouldFetch: boolean = true
): UseFetchUserRewardReportReturn {
  const reallyShouldFetch =
    userId?.trim() !== '' &&
    periodId?.trim() !== '' &&
    organizationId?.trim() !== '' &&
    token?.trim() !== '' &&
    shouldFetch

  const { data, error, isValidating, mutate } = useSWR(
    reallyShouldFetch
      ? [`rewards/users/${userId}/periods/${periodId}/report?organizationId=${organizationId}`, token]
      : null,
    ([url, tkn]: [string, string]) => fetchWithToken(url, tkn, null),
    {
      revalidateIfStale: true,
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      dedupingInterval: 0,
    }
  )

  const refetch = async (): Promise<UserRewardReport | null> => {
    const refetchedData = await mutate()
    return refetchedData as UserRewardReport | null
  }

  return {
    report: data as UserRewardReport | null,
    isLoading: !data && isValidating,
    isError: error,
    refetch,
  }
}

interface UseFetchUserRewardReportExpandedReturn {
  report: UserRewardReportExpanded | null
  isLoading: boolean
  isError: any
  refetch: () => Promise<UserRewardReportExpanded | null>
}

export function useFetchUserRewardReportExpanded(
  userId: string,
  periodId: string,
  organizationId: string,
  token: string,
  shouldFetch: boolean = true
): UseFetchUserRewardReportExpandedReturn {
  const reallyShouldFetch =
    userId?.trim() !== '' &&
    periodId?.trim() !== '' &&
    organizationId?.trim() !== '' &&
    token?.trim() !== '' &&
    shouldFetch

  const { data, error, isValidating, mutate } = useSWR(
    reallyShouldFetch
      ? [`rewards/users/${userId}/periods/${periodId}/report?organizationId=${organizationId}`, token]
      : null,
    ([url, tkn]: [string, string]) => fetchWithToken(url, tkn, null),
    {
      revalidateIfStale: true,
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      dedupingInterval: 0,
    }
  )

  const refetch = async (): Promise<UserRewardReportExpanded | null> => {
    const refetchedData = await mutate()
    return refetchedData as UserRewardReportExpanded | null
  }

  return {
    report: data as UserRewardReportExpanded | null,
    isLoading: !data && isValidating,
    isError: error,
    refetch,
  }
}

interface UseFetchAllUserRewardReportsReturn {
  reports: UserRewardReportExpanded[] | null
  currencySettings: OrganizationCurrencySettings | null
  isLoading: boolean
  isError: any
  refetch: () => Promise<AllUserRewardReportsResponse | null>
}

export function useFetchAllUserRewardReports(
  userId: string,
  organizationId: string,
  token: string,
  shouldFetch: boolean = true
): UseFetchAllUserRewardReportsReturn {
  const reallyShouldFetch =
    userId?.trim() !== '' && organizationId?.trim() !== '' && token?.trim() !== '' && shouldFetch

  const { data, error, isValidating, mutate } = useSWR<AllUserRewardReportsResponse>(
    reallyShouldFetch ? [`rewards/users/${userId}/all?organizationId=${organizationId}`, token] : null,
    ([url, tkn]: [string, string]) => fetchWithToken(url, tkn, null),
    {
      revalidateIfStale: true,
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      dedupingInterval: 0,
    }
  )

  const refetch = async (): Promise<AllUserRewardReportsResponse | null> => {
    const refetchedData = await mutate()
    return refetchedData || null
  }

  return {
    reports: data?.reports || null,
    currencySettings: data?.currencySettings || null,
    isLoading: !data && isValidating,
    isError: error,
    refetch,
  }
}

interface UseFetchEmployeeRewardsSummariesOptions {
  includeHidden?: boolean
  includeVisible?: boolean
  organizationIds?: string[]
  packageId?: string
  search?: string
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
  limit?: number
  offset?: number
}

interface UseFetchEmployeeRewardsSummariesReturn {
  summaries: EmployeeRewardsSummary[] | null
  totalCount: number
  currencySettings: OrganizationCurrencySettings | null
  isLoading: boolean
  isError: any
  refetch: () => Promise<EmployeeRewardsSummariesResponse | null>
}

export function useFetchEmployeeRewardsSummaries(
  organizationId: string,
  periodId: string,
  token: string,
  shouldFetch: boolean = true,
  options: UseFetchEmployeeRewardsSummariesOptions = {}
): UseFetchEmployeeRewardsSummariesReturn {
  const {
    includeHidden = true,
    includeVisible = true,
    organizationIds = [],
    packageId = '',
    search = '',
    sortBy = 'lastName',
    sortOrder = 'asc',
    limit = 25,
    offset = 0,
  } = options
  const reallyShouldFetch =
    organizationId?.trim() !== '' && periodId?.trim() !== '' && token?.trim() !== '' && shouldFetch

  const queryParams = new URLSearchParams({
    organizationId,
    periodId,
    includeHidden: String(includeHidden),
    includeVisible: String(includeVisible),
    sortBy,
    sortOrder,
    limit: String(limit),
    offset: String(offset),
  })

  // Add optional filters
  if (organizationIds.length > 0) {
    queryParams.set('organizationIds', organizationIds.join(','))
  }
  if (packageId) {
    queryParams.set('packageId', packageId)
  }
  if (search) {
    queryParams.set('search', search)
  }

  const { data, error, isValidating, mutate } = useSWR<EmployeeRewardsSummariesResponse>(
    reallyShouldFetch ? [`rewards/summaries?${queryParams.toString()}`, token] : null,
    ([url, tkn]: [string, string]) => fetchWithToken(url, tkn, null),
    {
      revalidateIfStale: true,
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      dedupingInterval: 0,
    }
  )

  const refetch = async (): Promise<EmployeeRewardsSummariesResponse | null> => {
    const refetchedData = await mutate()
    return refetchedData || null
  }

  return {
    summaries: data?.summaries || null,
    totalCount: data?.totalCount ?? 0,
    currencySettings: data?.currencySettings || null,
    isLoading: !data && isValidating,
    isError: error,
    refetch,
  }
}

interface UseCreateUserRewardReturn {
  createUserReward: (userId: string, periodId: string, dto: CreateUserRewardDTO) => Promise<UserReward>
  isLoading: boolean
  error: Error | null
}

export function useCreateUserReward(token: string): UseCreateUserRewardReturn {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const createUserReward = useCallback(
    (userId: string, periodId: string, dto: CreateUserRewardDTO): Promise<UserReward> => {
      setIsLoading(true)
      setError(null)

      return postWithToken(`rewards/users/${userId}/periods/${periodId}`, token, dto)
        .then((response) => {
          return response as UserReward
        })
        .catch((err) => {
          const error = err instanceof Error ? err : new Error('An error occurred')
          setError(error)
          throw error
        })
        .finally(() => {
          setIsLoading(false)
        })
    },
    [token]
  )

  return { createUserReward, isLoading, error }
}

interface UseUpdateUserRewardReturn {
  updateUserReward: (id: string, dto: UpdateUserRewardDTO) => Promise<void>
  isLoading: boolean
  error: Error | null
}

export function useUpdateUserReward(token: string): UseUpdateUserRewardReturn {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const updateUserReward = useCallback(
    (id: string, dto: UpdateUserRewardDTO): Promise<void> => {
      setIsLoading(true)
      setError(null)

      return putWithToken(`rewards/user-rewards/${id}`, token, dto)
        .then(() => {})
        .catch((err) => {
          const error = err instanceof Error ? err : new Error('An error occurred')
          setError(error)
          throw error
        })
        .finally(() => {
          setIsLoading(false)
        })
    },
    [token]
  )

  return { updateUserReward, isLoading, error }
}

interface UseDeleteUserRewardReturn {
  deleteUserReward: (id: string) => Promise<void>
  isLoading: boolean
  error: Error | null
}

export function useDeleteUserReward(token: string): UseDeleteUserRewardReturn {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const deleteUserReward = useCallback(
    (id: string): Promise<void> => {
      setIsLoading(true)
      setError(null)

      return deleteWithToken(`rewards/user-rewards/${id}`, token)
        .then(() => {})
        .catch((err) => {
          const error = err instanceof Error ? err : new Error('An error occurred')
          setError(error)
          throw error
        })
        .finally(() => {
          setIsLoading(false)
        })
    },
    [token]
  )

  return { deleteUserReward, isLoading, error }
}

interface SaveUserRewardsParams {
  userId: string
  periodId: string
  rootOrganizationId: string
  organizationId: string
  rootPeriodId: string
  rewards: UserRewardItem[]
  notes?: string
  packageId?: string
}

interface UseSaveUserRewardsReturn {
  saveUserRewards: (params: SaveUserRewardsParams) => Promise<void>
  isLoading: boolean
  error: Error | null
}

export function useSaveUserRewards(token: string): UseSaveUserRewardsReturn {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const saveUserRewards = useCallback(
    (params: SaveUserRewardsParams): Promise<void> => {
      setIsLoading(true)
      setError(null)

      const { userId, periodId, rootOrganizationId, organizationId, rootPeriodId, rewards, notes, packageId } = params

      // This endpoint will create a new revision with the provided rewards
      return postWithToken(`rewards/users/${userId}/periods/${periodId}`, token, {
        rootOrganizationId,
        organizationId,
        rootPeriodId,
        rewards,
        notes,
        packageId,
      })
        .then(() => {})
        .catch((err) => {
          const error = err instanceof Error ? err : new Error('An error occurred')
          setError(error)
          throw error
        })
        .finally(() => {
          setIsLoading(false)
        })
    },
    [token]
  )

  return { saveUserRewards, isLoading, error }
}

interface UseClearUserRewardsReturn {
  clearUserRewards: (userId: string, periodId: string) => Promise<void>
  isLoading: boolean
  error: Error | null
}

export function useClearUserRewards(token: string): UseClearUserRewardsReturn {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const clearUserRewards = useCallback(
    (userId: string, periodId: string): Promise<void> => {
      setIsLoading(true)
      setError(null)

      return deleteWithToken(`rewards/users/${userId}/periods/${periodId}`, token)
        .then(() => {})
        .catch((err) => {
          const error = err instanceof Error ? err : new Error('An error occurred')
          setError(error)
          throw error
        })
        .finally(() => {
          setIsLoading(false)
        })
    },
    [token]
  )

  return { clearUserRewards, isLoading, error }
}

// Helper to invalidate user rewards cache
export function invalidateUserRewardsCache(userId: string, periodId: string, token: string) {
  mutate([`rewards/users/${userId}/periods/${periodId}`, token])
  mutate([`rewards/users/${userId}/periods/${periodId}/report`, token])
}

export function invalidateEmployeeRewardsSummariesCache(organizationId: string, periodId: string, token: string) {
  mutate([`rewards/summaries?organizationId=${organizationId}&periodId=${periodId}`, token])
}
